import { useState } from 'react'
import { formatMoney, parseMoney } from '../../lib/money'
import { useStore } from '../../store'
import { Sheet } from '../../ui/parts'
import { addItem } from '../repo'

interface ScrapeResponse {
  title: string | null
  brand: string | null
  imageUrl: string | null
  priceFils: number | null
  currency: string | null
  confidence: string
  warnings: string[]
}

/**
 * Paste a link and let the shop fill the form in, or type everything by hand. Scraping
 * goes through /api/scrape because the browser cannot fetch another origin directly —
 * that Worker exists purely to sidestep CORS.
 */
export function AddItemSheet({ onClose }: { onClose: () => void }) {
  const { mutate, showToast } = useStore()

  const [url, setUrl] = useState('')
  const [title, setTitle] = useState('')
  const [brand, setBrand] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [price, setPrice] = useState('')
  const [currency, setCurrency] = useState<string | null>(null)
  const [warnings, setWarnings] = useState<string[]>([])
  const [fetching, setFetching] = useState(false)

  const parsed = parseMoney(price)
  const canSave = title.trim() !== '' && parsed !== null && parsed > 0

  async function fetchDetails() {
    setFetching(true)
    setWarnings([])
    try {
      const response = await fetch(`/api/scrape?url=${encodeURIComponent(url.trim())}`)
      const data = (await response.json()) as ScrapeResponse
      if (data.title && !title) setTitle(data.title)
      if (data.brand && !brand) setBrand(data.brand)
      if (data.imageUrl && !imageUrl) setImageUrl(data.imageUrl)
      if (data.priceFils && !price) setPrice(formatMoney(data.priceFils))
      setCurrency(data.currency)
      setWarnings(data.warnings ?? [])
    } catch {
      setWarnings(['Could not reach the shop — enter the details manually'])
    } finally {
      setFetching(false)
    }
  }

  async function save() {
    if (!canSave) return
    const saved = await mutate((s) =>
      addItem(s, {
        title,
        brand,
        imageUrl,
        sourceUrl: url,
        priceFils: parsed!,
        currencyRaw: currency,
      })
    )
    if (saved) {
      onClose()
      showToast(`Added "${title.trim()}"`)
    }
  }

  return (
    <Sheet onClose={onClose}>
      <h2 style={{ margin: '0 0 14px', fontSize: 20 }}>Add an item</h2>

      <label className="field">
        <span>Webshop link (optional)</span>
        <input
          type="url"
          inputMode="url"
          placeholder="https://…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </label>
      <button
        type="button"
        className="btn outline small"
        disabled={!url.trim() || fetching}
        onClick={() => void fetchDetails()}
      >
        {fetching ? 'Fetching…' : 'Fetch details'}
      </button>

      {warnings.map((warning) => (
        <div
          key={warning}
          className="verdict bad"
          style={{ marginTop: 8, fontSize: 13 }}
        >
          {warning}
        </div>
      ))}

      <div style={{ marginTop: 16 }}>
        <label className="field">
          <span>Title</span>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>

        <label className="field">
          <span>Brand</span>
          <input type="text" value={brand} onChange={(e) => setBrand(e.target.value)} />
        </label>

        <label className="field">
          <span>Price in AED</span>
          <input
            type="text"
            inputMode="decimal"
            value={price}
            className={price !== '' && (parsed === null || parsed <= 0) ? 'error' : ''}
            onChange={(e) => setPrice(e.target.value)}
          />
        </label>

        {currency && currency !== 'AED' ? (
          <div className="verdict bad" style={{ fontSize: 13, marginBottom: 10 }}>
            The shop listed this in {currency}. Nothing was converted — enter the AED amount
            yourself.
          </div>
        ) : null}

        <label className="field">
          <span>Image URL</span>
          <input
            type="url"
            inputMode="url"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
          />
        </label>
      </div>

      <div className="row" style={{ marginTop: 10 }}>
        <button type="button" className="btn grow" disabled={!canSave} onClick={() => void save()}>
          Add to list
        </button>
        <button type="button" className="btn ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  )
}
